import { useState } from 'react'
import {
  makeStyles,
  Popover,
  FormControlLabel,
  Checkbox,
} from '@material-ui/core'
import ExpandMoreIcon from '@material-ui/icons/ExpandMore'
import { useTranslation } from 'next-i18next'
import T from '../home-new/tokens'

// New-design-only horizontal filter/sort chip row for the ?new checkprice
// results (design mockup: "ส่งเร็วที่สุด ▾" + "เข้ารับหน้าบ้าน ▾" pills above
// the result list, instead of the old page's vertical FilterCard sidebar).
//
// This does NOT touch components/checkprice/FilterCard.js — that component
// stays exactly as-is for the old /checkprice page. This is wired to the
// exact same filterState/interFilterState/setFilterState/setInterFilterState
// props CheckpriceNew.js already owns, so no filtering logic is duplicated
// or changed — only the UI chrome around it differs.
//
// Feature parity note: the mockup only shows 2 chips (delivery-time framed
// as "ส่งเร็วที่สุด", and pickup-type framed as "เข้ารับหน้าบ้าน"). The old
// FilterCard also lets users filter by price range — dropping that would be
// a functional regression, so a third "ค่าบริการ" (price) chip is added
// here to preserve full filter parity. Each chip opens a Popover with the
// same checkbox options FilterCard.js renders for that group.

const useStyles = makeStyles((theme) => ({
  row: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '12px',
    marginBottom: 24,
    [theme.breakpoints.down('xs')]: {
      gap: '8px',
      marginBottom: 16,
    },
  },
  chip: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '6px',
    padding: '10px 18px',
    minHeight: 44,
    border: `1.5px solid ${T.color.brand}`,
    borderRadius: T.radius.pill,
    background: T.color.surface,
    color: T.color.brand,
    fontFamily: T.font.body,
    fontSize: 15,
    fontWeight: 500,
    cursor: 'pointer',
    touchAction: 'manipulation',
    transition: 'background 0.2s ease, color 0.2s ease',
    '& svg': {
      fontSize: 20,
    },
    '&:hover': {
      background: T.color.canvasAlt,
    },
    [theme.breakpoints.down('xs')]: {
      padding: '8px 14px',
      fontSize: 13,
    },
  },
  chipActive: {
    background: T.gradient.blue,
    borderColor: 'transparent',
    color: T.color.onBrand,
    '&:hover': {
      background: T.gradient.blue,
      filter: 'brightness(1.05)',
    },
  },
  clearLink: {
    display: 'inline-flex',
    alignItems: 'center',
    border: 'none',
    background: 'none',
    color: T.color.muted,
    fontFamily: T.font.body,
    fontSize: 14,
    textDecoration: 'underline',
    cursor: 'pointer',
    padding: '10px 4px',
    touchAction: 'manipulation',
  },
  popoverPaper: {
    padding: '16px 20px',
    borderRadius: T.radius.md,
    boxShadow: T.shadow.lg,
    fontFamily: T.font.body,
    minWidth: 220,
  },
  groupTitle: {
    margin: '0 0 4px',
    fontFamily: T.font.heading,
    fontWeight: 600,
    fontSize: 15,
    color: T.color.ink,
  },
  optionsList: {
    display: 'flex',
    flexDirection: 'column',
    '& .MuiFormControlLabel-label': {
      fontFamily: T.font.body,
    },
  },
  popoverClear: {
    marginTop: 8,
    alignSelf: 'flex-start',
    border: 'none',
    background: 'none',
    color: T.color.brand,
    fontFamily: T.font.body,
    fontSize: 13,
    textDecoration: 'underline',
    cursor: 'pointer',
    padding: 0,
    touchAction: 'manipulation',
  },
}))

const FilterChip = ({ label, options, activeState, onChange, onClear }) => {
  const classes = useStyles()
  const { t } = useTranslation('checkprice')
  const [anchorEl, setAnchorEl] = useState(null)
  const open = Boolean(anchorEl)
  const isActive = options.some(([name]) => activeState[name])

  return (
    <>
      <button
        type="button"
        className={`${classes.chip} ${isActive ? classes.chipActive : ''}`}
        onClick={(e) => setAnchorEl(e.currentTarget)}
        aria-haspopup="true"
        aria-expanded={open}
      >
        {label}
        <ExpandMoreIcon />
      </button>
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        classes={{ paper: classes.popoverPaper }}
      >
        <h4 className={classes.groupTitle}>{label}</h4>
        <div className={classes.optionsList}>
          {options.map(([name, optLabel]) => (
            <FormControlLabel
              key={name}
              control={
                <Checkbox
                  color="primary"
                  checked={!!activeState[name]}
                  onChange={onChange}
                  name={name}
                />
              }
              label={optLabel}
            />
          ))}
        </div>
        {isActive && (
          <button type="button" className={classes.popoverClear} onClick={onClear}>
            {t('checkpriceForm.filterClearThis')}
          </button>
        )}
      </Popover>
    </>
  )
}

const FilterBarNew = ({
  filterState,
  setFilterState,
  interFilterState,
  setInterFilterState,
  deliveryType,
}) => {
  const classes = useStyles()
  const { t } = useTranslation('checkprice')

  const isInter = deliveryType === 'inter'
  const activeState = isInter ? interFilterState : filterState
  const setActiveState = isInter ? setInterFilterState : setFilterState

  const priceOptions = isInter
    ? [
        ['p150to500', `150 ${t('baht')} - 500 ${t('baht')}`],
        ['p501to1000', `501 ${t('baht')} - 1000 ${t('baht')}`],
        ['p1001to2000', `1001 ${t('baht')} - 2000 ${t('baht')}`],
        ['p2000up', `${t('more-than')} 2000 ${t('baht')}`],
      ]
    : [
        ['p50down', `${t('less-than')} 50 ${t('baht')}`],
        ['p50to100', `50 ${t('baht')} - 100 ${t('baht')}`],
        ['p101to150', `101 ${t('baht')} - 150 ${t('baht')}`],
        ['p150up', `${t('more-than')} 150 ${t('baht')}`],
      ]

  const timeOptions = [
    ['d1down', `1 ${t('day')}`],
    ['d2to3', `2 - 3 ${t('days')}`],
    ['d4to5', `4 - 5 ${t('days')}`],
    ['d6to7', `6 - 7 ${t('days')}`],
    ['d8to14', `8 - 14 ${t('days')}`],
    ['d15up', `15 ${t('days')} ${t('or-more')}`],
  ]

  const pickupOptions = [
    ['pickup', t('pick-up')],
    ['dropoff', t('drop-off')],
  ]

  const handleChange = (e) => {
    setActiveState({ ...activeState, [e.target.name]: e.target.checked })
  }

  const clearGroup = (names) => {
    const cleared = {}
    names.forEach((n) => {
      cleared[n] = false
    })
    setActiveState({ ...activeState, ...cleared })
  }

  const allActive = [...priceOptions, ...timeOptions, ...pickupOptions].some(
    ([name]) => activeState[name]
  )

  return (
    <div className={classes.row} role="group" aria-label={t('filter')}>
      <FilterChip
        label={t('checkpriceForm.filterFastest')}
        options={timeOptions}
        activeState={activeState}
        onChange={handleChange}
        onClear={() => clearGroup(timeOptions.map(([n]) => n))}
      />
      <FilterChip
        label={t('checkpriceForm.filterPickup')}
        options={pickupOptions}
        activeState={activeState}
        onChange={handleChange}
        onClear={() => clearGroup(pickupOptions.map(([n]) => n))}
      />
      <FilterChip
        label={t('prices-range')}
        options={priceOptions}
        activeState={activeState}
        onChange={handleChange}
        onClear={() => clearGroup(priceOptions.map(([n]) => n))}
      />
      {allActive && (
        <button
          type="button"
          className={classes.clearLink}
          onClick={() =>
            clearGroup([
              ...priceOptions.map(([n]) => n),
              ...timeOptions.map(([n]) => n),
              ...pickupOptions.map(([n]) => n),
            ])
          }
        >
          {t('checkpriceForm.filterClearAll')}
        </button>
      )}
    </div>
  )
}

export default FilterBarNew
